import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box, TextField, Button, Paper, Typography, CircularProgress,
  List, ListItem, ListItemButton, ListItemText, Divider, Chip, Grid, Collapse,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import GavelIcon from '@mui/icons-material/Gavel';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import TimerIcon from '@mui/icons-material/Timer';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { searchSenateStandingOrders, getSenateStandingOrder, getSenateStandingOrdersToc } from '../services/api';
import SearchSkeleton from './SearchSkeleton';
import EmptyState from './EmptyState';
import ErrorState from './ErrorState';

// Component to render a single standing order content
function StandingOrderContent({ standingOrder, isFirst, themeColor = '#9c27b0' }) {
  return (
    <Box
      id={`so-${standingOrder.so_number}`}
      sx={{
        scrollMarginTop: '80px',
        mb: 3,
        pb: 3,
        borderBottom: '1px solid',
        borderColor: 'divider',
        '&:last-child': { borderBottom: 'none' }
      }}
    >
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
        {isFirst && <GavelIcon sx={{ color: themeColor, fontSize: 28 }} />}
        <Chip label={`SO ${standingOrder.so_number}`} size="small" sx={{ bgcolor: themeColor, color: 'white' }} />
        <Typography variant={isFirst ? 'h5' : 'h6'} sx={{ fontWeight: 600, color: themeColor }}>
          {standingOrder.title?.replace(`Standing Order ${standingOrder.so_number}: `, '') || 'Untitled'}
        </Typography>
      </Box>

      {standingOrder.chapter_number && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
          <Chip label={`Chapter ${standingOrder.chapter_number}`} variant="outlined" size="small" />
          {standingOrder.chapter_title && (
            <Chip label={standingOrder.chapter_title} variant="outlined" size="small" />
          )}
        </Box>
      )}

      {/* Content - render HTML if available, otherwise plain text */}
      {standingOrder.content_html ? (
        <Box
          sx={{
            '& p': { mb: 2, lineHeight: 1.8 },
            '& ol': { pl: 3, mb: 2, listStyleType: 'decimal' },
            '& ul': { pl: 3, mb: 2, listStyleType: 'disc' },
            '& li': { mb: 1, lineHeight: 1.7 },
            '& ol ol': { listStyleType: 'lower-alpha', mt: 1 },
            '& ol ol ol': { listStyleType: 'lower-roman' },
            '& .bracketed-list': { listStyleType: 'none', counterReset: 'item' },
            '& .bracketed-list > li': { counterIncrement: 'item' },
            '& .bracketed-list > li::before': { content: '"(" counter(item) ") "', fontWeight: 500 },
            // Table styling
            '& table': { width: '100%', borderCollapse: 'collapse', mb: 2 },
            '& th, & td': { padding: '8px', border: '1px solid #ddd', lineHeight: 1.6 },
            '& th': { backgroundColor: '#f5f5f5', fontWeight: 600 },
            '& thead tr': { backgroundColor: '#f5f5f5' },
            // Definition list styling
            '& dl': { mb: 2 },
            '& dt': { fontWeight: 600, mt: 1.5, color: '#333' },
            '& dd': { ml: 2, mb: 1, color: '#555', lineHeight: 1.7 }
          }}
          dangerouslySetInnerHTML={{ __html: standingOrder.content_html }}
        />
      ) : (
        <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.8 }}>
          {standingOrder.content}
        </Typography>
      )}

      {/* Cross-references */}
      {standingOrder.cross_references?.length > 0 && standingOrder.cross_references[0] !== standingOrder.so_number && (
        <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
            Cross-references
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {standingOrder.cross_references.filter(ref => ref !== standingOrder.so_number).map((ref, idx) => (
              <Chip
                key={idx}
                label={`SO ${ref}`}
                size="small"
                variant="outlined"
                sx={{ cursor: 'pointer' }}
              />
            ))}
          </Box>
        </Box>
      )}

      {/* Source link */}
      {standingOrder.source_url && (
        <Box sx={{ mt: 2 }}>
          <Button
            size="small"
            href={standingOrder.source_url}
            target="_blank"
            rel="noopener noreferrer"
            sx={{ color: themeColor }}
          >
            View on APH Website
          </Button>
        </Box>
      )}
    </Box>
  );
}

export default function SenateStandingOrdersSearch() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [query, setQuery] = useState(searchParams.get('q') || '');
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [error, setError] = useState(null);
  const [toc, setToc] = useState(null);
  const [tocLoading, setTocLoading] = useState(true);
  const [expandedChapters, setExpandedChapters] = useState({});
  const [selectedSos, setSelectedSos] = useState([]); // Array of loaded standing orders
  const [loadingContent, setLoadingContent] = useState(false);
  const [activeChapter, setActiveChapter] = useState(null);
  const [activeSoNumber, setActiveSoNumber] = useState(null);
  const [showPreamble, setShowPreamble] = useState(false);
  const contentRef = useRef(null);

  const THEME_COLOR = '#b0182b';

  // Handle preamble click
  const handlePreambleClick = () => {
    setShowPreamble(true);
    setActiveChapter(null);
    setActiveSoNumber(null);
    setSelectedSos([]);
    if (contentRef.current) {
      contentRef.current.scrollTop = 0;
    }
  };

  // Load TOC on mount
  useEffect(() => {
    const loadToc = async () => {
      setTocLoading(true);
      try {
        const data = await getSenateStandingOrdersToc();
        setToc(data);
        // Auto-expand first chapter
        if (data.chapters?.length > 0) {
          setExpandedChapters({ [data.chapters[0].chapter_number]: true });
        }
      } catch (err) {
        console.error('Failed to load TOC:', err);
      }
      setTocLoading(false);
    };
    loadToc();
  }, []);

  // Initial content load - depends on TOC being loaded
  useEffect(() => {
    if (!toc) return; // Wait for TOC to load

    const urlQuery = searchParams.get('q');
    if (urlQuery && urlQuery !== '*') {
      handleSearch(urlQuery);
    } else {
      // Load first chapter's standing orders by default
      const firstChapter = toc.chapters?.[0];
      if (firstChapter) {
        loadChapterSosFromData(firstChapter);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [toc]);

  const handleSearch = async (searchQuery = null) => {
    const q = searchQuery !== null ? searchQuery : (query || '*');
    setLoading(true);
    setError(null);
    try {
      const data = await searchSenateStandingOrders(q, 1, 100);
      setResults(data);
      setSearchParams({ q });

      // Load content for first few search results
      if (data.hits?.length > 0) {
        const firstHits = data.hits.slice(0, 5);
        setSelectedSos(firstHits);
        setActiveSoNumber(firstHits[0]?.so_number);
        setActiveChapter(null);
      }
    } catch (err) {
      console.error('Search error:', err);
      setError(err.message || 'Failed to search. Please try again.');
      setResults(null);
    }
    setLoading(false);
    setInitialLoading(false);
  };

  const handleReset = () => {
    setQuery('');
    setResults(null);
    setSearchParams({});
    loadChapterSos(1);
  };

  const toggleChapter = (chapterNum) => {
    setExpandedChapters(prev => ({
      ...prev,
      [chapterNum]: !prev[chapterNum]
    }));
  };

  // Load all standing orders for a chapter (from chapter data)
  const loadChapterSosFromData = useCallback(async (chapter) => {
    if (!chapter) return;

    setLoadingContent(true);
    setActiveChapter(chapter.chapter_number);
    setActiveSoNumber(null);

    try {
      const loadedSos = [];
      for (const so of chapter.standing_orders || []) {
        const fullSo = await getSenateStandingOrder(so.so_number);
        loadedSos.push(fullSo);
      }
      setSelectedSos(loadedSos);

      // Scroll to top of content
      if (contentRef.current) {
        contentRef.current.scrollTop = 0;
      }
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      console.error('Error loading chapter:', err);
    }
    setLoadingContent(false);
    setInitialLoading(false);
  }, []);

  // Load all standing orders for a chapter (by chapter number)
  const loadChapterSos = useCallback(async (chapterNum) => {
    if (!toc) return;

    const chapter = toc.chapters?.find(c => c.chapter_number === chapterNum);
    if (!chapter) return;

    await loadChapterSosFromData(chapter);
  }, [toc, loadChapterSosFromData]);

  // Load a single standing order
  const loadSingleSo = useCallback(async (soNumber) => {
    setLoadingContent(true);
    setActiveSoNumber(soNumber);
    setActiveChapter(null);

    try {
      const fullSo = await getSenateStandingOrder(soNumber);
      setSelectedSos([fullSo]);

      // Scroll to top
      if (contentRef.current) {
        contentRef.current.scrollTop = 0;
      }
    } catch (err) {
      console.error('Error loading standing order:', err);
    }
    setLoadingContent(false);
    setInitialLoading(false);
  }, []);

  // Handle chapter header click
  const handleChapterClick = (chapterNum) => {
    setShowPreamble(false);
    loadChapterSos(chapterNum);
    setExpandedChapters(prev => ({ ...prev, [chapterNum]: true }));
  };

  // Handle individual SO click
  const handleSoClick = (soNumber, chapterNum) => {
    setShowPreamble(false);
    loadSingleSo(soNumber);
    setExpandedChapters(prev => ({ ...prev, [chapterNum]: true }));
  };

  // Get count of SOs in a chapter
  const getChapterSoCount = (chapter) => {
    return chapter.standing_orders?.length || 0;
  };

  // Check if a chapter is currently loaded
  const isChapterLoaded = (chapterNum) => {
    return activeChapter === chapterNum;
  };

  // Check if a specific SO is currently loaded
  const isSoLoaded = (soNumber) => {
    return selectedSos.some(so => so.so_number === soNumber);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
        <GavelIcon sx={{ color: THEME_COLOR, fontSize: 32 }} />
        <Typography variant="h4">Senate Standing Orders</Typography>
      </Box>

      {/* Search Box */}
      <Paper elevation={1} sx={{ p: 2, mb: 3, borderRadius: 3 }}>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <TextField
            fullWidth
            placeholder="Search Senate Standing Orders..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
            variant="outlined"
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 3 } }}
          />
          <Button
            variant="contained"
            onClick={() => handleSearch()}
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : <SearchIcon />}
            sx={{ borderRadius: 3, px: 4, bgcolor: THEME_COLOR, '&:hover': { bgcolor: '#7b1fa2' } }}
          >
            Search
          </Button>
          <Button variant="outlined" onClick={handleReset} sx={{ borderRadius: 3 }}>
            Reset
          </Button>
        </Box>
        {results && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {results.total.toLocaleString()} standing orders found
          </Typography>
        )}
      </Paper>

      {initialLoading && <SearchSkeleton count={5} />}

      {!initialLoading && error && (
        <ErrorState message={error} onRetry={() => handleSearch()} themeColor={THEME_COLOR} />
      )}

      {!initialLoading && !error && results && results.total === 0 && (
        <EmptyState
          query={query}
          type="Senate Standing Orders"
          onSuggestionClick={(q) => { setQuery(q); handleSearch(q); }}
          onReset={handleReset}
          themeColor={THEME_COLOR}
        />
      )}

      {!initialLoading && !error && (
        <Grid container spacing={3}>
          {/* Table of Contents Sidebar */}
          <Grid item xs={12} md={4}>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                borderRadius: 3,
                border: '1px solid',
                borderColor: 'divider',
                position: 'sticky',
                top: 16,
                maxHeight: 'calc(100vh - 200px)',
                overflow: 'auto'
              }}
            >
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                Table of Contents
              </Typography>

              {tocLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress size={24} />
                </Box>
              ) : toc ? (
                <List dense disablePadding>
                  {/* Preamble section */}
                  {toc.preamble && (
                    <>
                      <ListItem disablePadding>
                        <ListItemButton
                          onClick={handlePreambleClick}
                          sx={{
                            py: 0.5,
                            borderRadius: 1,
                            borderLeft: showPreamble ? `3px solid ${THEME_COLOR}` : '3px solid transparent',
                            bgcolor: showPreamble ? 'rgba(176, 24, 43, 0.08)' : 'transparent',
                            '&:hover': { bgcolor: 'action.hover' }
                          }}
                        >
                          <TimerIcon sx={{ fontSize: 18, mr: 1, color: showPreamble ? THEME_COLOR : 'text.secondary' }} />
                          <ListItemText
                            primary={
                              <Typography
                                variant="body2"
                                sx={{
                                  fontWeight: 600,
                                  fontSize: '0.85rem',
                                  color: showPreamble ? THEME_COLOR : 'text.primary'
                                }}
                              >
                                {toc.preamble.title}
                              </Typography>
                            }
                          />
                        </ListItemButton>
                      </ListItem>
                      <Divider sx={{ my: 0.5 }} />
                    </>
                  )}
                  {toc.chapters?.map((chapter) => {
                    const chapterLoaded = isChapterLoaded(chapter.chapter_number);
                    const hasSomeSosLoaded = chapter.standing_orders?.some(so => isSoLoaded(so.so_number));

                    return (
                      <React.Fragment key={chapter.chapter_number}>
                        <ListItem disablePadding>
                          <ListItemButton
                            onClick={() => handleChapterClick(chapter.chapter_number)}
                            sx={{
                              py: 0.5,
                              borderRadius: 1,
                              borderLeft: chapterLoaded ? `3px solid ${THEME_COLOR}` : hasSomeSosLoaded ? '3px solid #e0e0e0' : '3px solid transparent',
                              bgcolor: chapterLoaded ? 'rgba(176, 24, 43, 0.08)' : 'transparent',
                              '&:hover': { bgcolor: 'action.hover' }
                            }}
                          >
                            <ListItemText
                              primary={
                                <Typography
                                  variant="body2"
                                  sx={{
                                    fontWeight: 600,
                                    fontSize: '0.85rem',
                                    color: chapterLoaded ? THEME_COLOR : 'text.primary'
                                  }}
                                >
                                  Ch. {chapter.chapter_number}: {chapter.chapter_title || 'Untitled'}
                                  <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                                    ({getChapterSoCount(chapter)})
                                  </Typography>
                                </Typography>
                              }
                            />
                            <Box
                              onClick={(e) => { e.stopPropagation(); toggleChapter(chapter.chapter_number); }}
                              sx={{ ml: 1, display: 'flex', alignItems: 'center' }}
                            >
                              {expandedChapters[chapter.chapter_number] ? (
                                <ExpandLess fontSize="small" />
                              ) : (
                                <ExpandMore fontSize="small" />
                              )}
                            </Box>
                          </ListItemButton>
                        </ListItem>
                        <Collapse in={expandedChapters[chapter.chapter_number]}>
                          <List dense disablePadding sx={{ pl: 1 }}>
                            {chapter.standing_orders?.map((so) => {
                              const isCurrent = activeSoNumber === so.so_number;
                              const isLoaded = isSoLoaded(so.so_number);
                              return (
                                <ListItem key={so.so_id} disablePadding>
                                  <ListItemButton
                                    onClick={() => handleSoClick(so.so_number, chapter.chapter_number)}
                                    selected={isCurrent}
                                    sx={{
                                      py: 0.25,
                                      pl: 2,
                                      borderLeft: isCurrent ? `3px solid ${THEME_COLOR}` : isLoaded ? '3px solid #e0e0e0' : '3px solid transparent',
                                      '&.Mui-selected': {
                                        bgcolor: 'rgba(176, 24, 43, 0.08)'
                                      },
                                      '&:hover': { bgcolor: 'action.hover' }
                                    }}
                                  >
                                    <ListItemText
                                      primary={
                                        <Typography
                                          variant="body2"
                                          sx={{
                                            fontSize: '0.8rem',
                                            fontWeight: isCurrent ? 600 : 400,
                                            color: isCurrent ? THEME_COLOR : isLoaded ? '#666' : 'text.secondary'
                                          }}
                                        >
                                          SO {so.so_number}: {so.title?.substring(0, 35)}{so.title?.length > 35 ? '...' : ''}
                                        </Typography>
                                      }
                                    />
                                  </ListItemButton>
                                </ListItem>
                              );
                            })}
                          </List>
                        </Collapse>
                        <Divider sx={{ my: 0.5 }} />
                      </React.Fragment>
                    );
                  })}
                </List>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Failed to load table of contents
                </Typography>
              )}
            </Paper>
          </Grid>

          {/* Main Content */}
          <Grid item xs={12} md={8}>
            <Paper ref={contentRef} elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
              {/* Header showing what's currently displayed */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                {showPreamble ? (
                  <TimerIcon sx={{ color: THEME_COLOR, fontSize: 28 }} />
                ) : (
                  <GavelIcon sx={{ color: THEME_COLOR, fontSize: 28 }} />
                )}
                <Typography variant="h5" sx={{ fontWeight: 600, color: THEME_COLOR }}>
                  {showPreamble
                    ? toc?.preamble?.title || 'Time limits on debate'
                    : activeChapter
                      ? `Chapter ${activeChapter}`
                      : activeSoNumber
                        ? `Standing Order ${activeSoNumber}`
                        : 'Senate Standing Orders'}
                </Typography>
                {selectedSos.length > 1 && (
                  <Chip
                    label={`${selectedSos.length} standing orders`}
                    variant="outlined"
                    size="small"
                    color="secondary"
                  />
                )}
              </Box>

              {/* Loading overlay */}
              {loadingContent && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress size={24} />
                </Box>
              )}

              {/* Preamble content */}
              {showPreamble && toc?.preamble && (
                <Box>
                  <Typography variant="body1" sx={{ mb: 3, color: 'text.secondary' }}>
                    {toc.preamble.introduction}
                  </Typography>

                  {/* Time limits table */}
                  <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ bgcolor: 'rgba(176, 24, 43, 0.08)' }}>
                          <TableCell sx={{ fontWeight: 600 }}>Category</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Time Limit</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Standing Order</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {toc.preamble.time_limits?.map((item, idx) => {
                          // Extract SO number from string like "SO 189(1)" -> "189"
                          const soMatch = item.standing_order.match(/SO\s*(\d+[A-Z]?)/i);
                          const soNumber = soMatch ? soMatch[1] : null;

                          return (
                            <TableRow key={idx} sx={{ '&:nth-of-type(odd)': { bgcolor: 'action.hover' } }}>
                              <TableCell>{item.category}</TableCell>
                              <TableCell>
                                <Chip label={item.time_limit} size="small" variant="outlined" sx={{ fontWeight: 500 }} />
                              </TableCell>
                              <TableCell>
                                {soNumber ? (
                                  <Button
                                    size="small"
                                    onClick={() => {
                                      setShowPreamble(false);
                                      loadSingleSo(soNumber);
                                      // Also expand the chapter containing this SO
                                      const chapter = toc.chapters?.find(ch =>
                                        ch.standing_orders?.some(so => so.so_number === soNumber)
                                      );
                                      if (chapter) {
                                        setExpandedChapters(prev => ({ ...prev, [chapter.chapter_number]: true }));
                                      }
                                    }}
                                    sx={{
                                      color: THEME_COLOR,
                                      fontWeight: 500,
                                      textTransform: 'none',
                                      minWidth: 'auto',
                                      p: 0.5,
                                      '&:hover': {
                                        bgcolor: 'rgba(176, 24, 43, 0.08)',
                                        textDecoration: 'underline'
                                      }
                                    }}
                                  >
                                    {item.standing_order}
                                  </Button>
                                ) : (
                                  <Typography variant="body2" sx={{ color: THEME_COLOR, fontWeight: 500 }}>
                                    {item.standing_order}
                                  </Typography>
                                )}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  {/* Senate Numbers */}
                  {toc.preamble.senate_numbers && (
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <InfoOutlinedIcon fontSize="small" />
                        Senate Numbers
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                        <Paper variant="outlined" sx={{ p: 2, textAlign: 'center', minWidth: 120 }}>
                          <Typography variant="h4" sx={{ color: THEME_COLOR, fontWeight: 700 }}>
                            {toc.preamble.senate_numbers.quorum}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">Quorum</Typography>
                        </Paper>
                        <Paper variant="outlined" sx={{ p: 2, textAlign: 'center', minWidth: 120 }}>
                          <Typography variant="h4" sx={{ color: THEME_COLOR, fontWeight: 700 }}>
                            {toc.preamble.senate_numbers.absolute_majority}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">Absolute Majority</Typography>
                        </Paper>
                      </Box>
                    </Box>
                  )}

                  {/* Note */}
                  {toc.preamble.note && (
                    <Paper variant="outlined" sx={{ p: 2, bgcolor: 'action.hover' }}>
                      <Typography variant="body2" sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
                        <strong>Note:</strong> {toc.preamble.note}
                      </Typography>
                    </Paper>
                  )}
                </Box>
              )}

              {/* Render all loaded standing orders */}
              {!loadingContent && !showPreamble && selectedSos.length > 0 && (
                <>
                  {selectedSos.map((so, idx) => (
                    <StandingOrderContent
                      key={so.so_id || so.id || so.so_number}
                      standingOrder={so}
                      isFirst={idx === 0}
                      themeColor={THEME_COLOR}
                    />
                  ))}

                  {/* End indicator when showing multiple */}
                  {selectedSos.length > 1 && (
                    <Box sx={{ textAlign: 'center', py: 2, color: 'text.secondary' }}>
                      <Typography variant="caption">
                        Showing {selectedSos.length} standing orders
                        {activeChapter ? ` from Chapter ${activeChapter}` : ''}
                      </Typography>
                    </Box>
                  )}
                </>
              )}

              {/* Empty state */}
              {!loadingContent && !showPreamble && selectedSos.length === 0 && (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Typography variant="body1" color="text.secondary">
                    Select a chapter or standing order from the table of contents to view its content.
                  </Typography>
                </Box>
              )}
            </Paper>
          </Grid>
        </Grid>
      )}
    </Box>
  );
}
